"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Box,
  ColorSwatch,
  SimpleGrid,
  Divider,
  Image,
  Accordion,
  List,
  Badge,
} from "@mantine/core";
import { IconCheck, IconX, IconChevronRight } from "@tabler/icons-react";
import Link from "next/link";
import { useState } from "react";
import { StyleEssence, styleEssences, essenceIds } from "../essenceData";
import QuizPhotoUpload from "@/components/QuizPhotoUpload";

interface StyleEssenceContentProps {
  essenceData: StyleEssence;
  lastUpdated: string;
}

export default function StyleEssenceContent({ essenceData, lastUpdated }: StyleEssenceContentProps) {
  const [currentOutfitIndex, setCurrentOutfitIndex] = useState(0);

  return (
    <Container size="md" py="xl">
      {/* Clean Header */}
      <Box mb="xl">
        <Text size="sm" c="dimmed" mb={4}>
          Style Essence Guide
        </Text>
        <Title order={1} fz={{ base: 32, md: 42 }} fw={600} mb="sm">
          {essenceData.name} Style Essence
        </Title>
        <Text fz="lg" c="dimmed" fs="italic" mb="md">
          {essenceData.tagline}
        </Text>
        <Group gap="md">
          <Badge variant="light" color="gray" size="lg">
            {essenceData.yinYangLevel}
          </Badge>
          <Text size="sm" c="dimmed">
            Updated {new Date(lastUpdated).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </Text>
        </Group>
      </Box>

      {/* Hero Image */}
      <Box mb="xl">
        <Image
          src={`/style-essence/types/${essenceData.id}-hero.jpg`}
          alt={`${essenceData.name} style essence featuring ${essenceData.energyWords.slice(0, 3).join(", ").toLowerCase()} energy`}
          radius="md"
          fallbackSrc={`https://placehold.co/1200x600/f8f8f8/999?text=${encodeURIComponent(essenceData.name)}`}
        />
      </Box>

      {/* Introduction */}
      <Box mb="xl">
        <Text fz="lg" style={{ lineHeight: 1.8 }} mb="lg">
          {essenceData.description}
        </Text>
        <Text style={{ lineHeight: 1.8, whiteSpace: "pre-line" }} c="dimmed">
          {essenceData.extendedDescription}
        </Text>
      </Box>

      <Divider my="xl" />

      {/* Facial Features */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          {essenceData.name} Facial Features
        </Title>
        <Text c="dimmed" mb="md">
          People with {essenceData.name} essence typically have:
        </Text>
        <List spacing="sm" size="md">
          {essenceData.facialFeatures.map((feature, i) => (
            <List.Item key={i}>{feature}</List.Item>
          ))}
        </List>
      </Box>

      {/* Body Characteristics */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          Body Characteristics
        </Title>
        <Text c="dimmed" mb="md">
          Common physical traits associated with {essenceData.name} essence:
        </Text>
        <List spacing="sm" size="md">
          {essenceData.bodyCharacteristics.map((char, i) => (
            <List.Item key={i}>{char}</List.Item>
          ))}
        </List>
      </Box>

      {/* Energy Words */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          {essenceData.name} Energy
        </Title>
        <Text c="dimmed" mb="md">
          Words that describe the presence and impression of {essenceData.name} essence:
        </Text>
        <Text fz="lg">
          {essenceData.energyWords.join(" · ")}
        </Text>
      </Box>

      <Divider my="xl" />

      {/* Color Palette */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          Best Colors for {essenceData.name} Essence
        </Title>
        <Text c="dimmed" mb="lg">
          These colors complement {essenceData.name} energy:
        </Text>
        <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="lg">
          {essenceData.colorPalette.map((color) => (
            <Box key={color.name}>
              <Group gap="sm" mb={4}>
                <ColorSwatch color={color.hex} size={24} radius="sm" />
                <Text fw={500}>{color.name}</Text>
              </Group>
              <Text size="sm" c="dimmed">{color.description}</Text>
            </Box>
          ))}
        </SimpleGrid>
        <Box mt="lg" p="md" bg="gray.0" style={{ borderRadius: 8 }}>
          <Text size="sm">
            <strong>Color Season Connection:</strong> {essenceData.seasonConnection}{" "}
            <Link href="/color-analysis-quiz" style={{ color: "inherit" }}>
              Find your color season →
            </Link>
          </Text>
        </Box>
      </Box>

      <Divider my="xl" />

      {/* Outfit Ideas */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="lg">
          {essenceData.name} Outfit Ideas
        </Title>

        {essenceData.outfitIdeas.map((outfit, index) => (
          <Box key={index} mb="xl">
            <Image
              src={`/style-essence/types/${essenceData.id}-outfit-${index + 1}.jpg`}
              alt={`${essenceData.name} essence ${outfit.name} outfit featuring ${outfit.pieces.slice(0, 2).join(" and ")}`}
              radius="md"
              mb="md"
              fallbackSrc={`https://placehold.co/600x700/f8f8f8/999?text=${encodeURIComponent(outfit.name)}`}
            />
            <Text size="sm" c="dimmed" mb={4}>{outfit.occasion}</Text>
            <Title order={3} fz="lg" mb="xs">{outfit.name}</Title>
            <Text c="dimmed" mb="md">{outfit.description}</Text>
            <List spacing="xs" size="sm">
              {outfit.pieces.map((piece, i) => (
                <List.Item key={i}>{piece}</List.Item>
              ))}
            </List>
          </Box>
        ))}
      </Box>

      <Divider my="xl" />

      {/* Key Wardrobe Pieces */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          Essential {essenceData.name} Wardrobe Pieces
        </Title>
        <List spacing="sm" size="md">
          {essenceData.keyPieces.map((piece, i) => (
            <List.Item key={i}>{piece}</List.Item>
          ))}
        </List>
      </Box>

      {/* Fabrics & Textures */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          Fabrics & Textures
        </Title>
        <Text>
          {essenceData.fabricsAndTextures.join(", ")}
        </Text>
      </Box>

      {/* Patterns & Prints */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          Patterns & Prints
        </Title>
        <Text>
          {essenceData.patternsAndPrints.join(", ")}
        </Text>
      </Box>

      {/* Accessories */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          Accessories
        </Title>
        <Text>
          {essenceData.accessories.join(", ")}
        </Text>
      </Box>

      <Divider my="xl" />

      {/* Do's and Don'ts */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" mb="xl">
        <Box>
          <Title order={2} fz="xl" mb="md" c="green.7">
            What Works
          </Title>
          <List spacing="sm" icon={<IconCheck size={16} color="var(--mantine-color-green-6)" />}>
            {essenceData.styleKeywords.slice(0, 6).map((keyword, i) => (
              <List.Item key={i}>{keyword} styling</List.Item>
            ))}
          </List>
        </Box>
        <Box>
          <Title order={2} fz="xl" mb="md" c="red.7">
            What to Avoid
          </Title>
          <List spacing="sm" icon={<IconX size={16} color="var(--mantine-color-red-6)" />}>
            {essenceData.avoidStyles.map((avoid, i) => (
              <List.Item key={i}>{avoid}</List.Item>
            ))}
          </List>
        </Box>
      </SimpleGrid>

      <Divider my="xl" />

      {/* Makeup & Hair */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          {essenceData.name} Makeup
        </Title>
        <Text style={{ lineHeight: 1.8 }}>{essenceData.makeupStyle}</Text>
      </Box>

      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          Hairstyles
        </Title>
        <Text>{essenceData.hairstyles.join(", ")}</Text>
      </Box>

      <Divider my="xl" />

      {/* Celebrities */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          {essenceData.name} Celebrity Examples
        </Title>
        <Text c="dimmed" mb="md">
          Study these style icons for inspiration:
        </Text>
        <Stack gap="md">
          {essenceData.celebrities.map((celeb, i) => (
            <Box key={i}>
              <Text fw={600}>{celeb.name}</Text>
              <Text size="sm" c="dimmed">{celeb.description}</Text>
            </Box>
          ))}
        </Stack>
      </Box>

      <Divider my="xl" />

      {/* Common Blends */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          Common {essenceData.name} Blends
        </Title>
        <Text c="dimmed" mb="md">
          Most people have 2-3 essences. Here are common blends with {essenceData.name}:
        </Text>
        <Stack gap="md">
          {essenceData.commonBlends.map((blend, i) => {
            const blendEssenceId = blend.essence.toLowerCase().replace(" ", "-");
            return (
              <Box key={i}>
                <Text fw={600}>
                  <Link href={`/style-essence/${blendEssenceId}`} style={{ color: "inherit" }}>
                    {blend.essence}
                  </Link>
                  {" + "}{essenceData.name}
                </Text>
                <Text size="sm" c="dimmed">{blend.description}</Text>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* vs Other Essences */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          {essenceData.name} vs Other Essences
        </Title>
        <Text c="dimmed" mb="md">
          Understanding the differences helps you identify your true essence:
        </Text>
        <Stack gap="md">
          {essenceData.vsOtherEssences.map((vs, i) => {
            const vsEssenceId = vs.essence.toLowerCase().replace(" ", "-");
            return (
              <Box key={i}>
                <Text fw={600}>
                  {essenceData.name} vs{" "}
                  <Link href={`/style-essence/${vsEssenceId}`} style={{ color: "inherit" }}>
                    {vs.essence}
                  </Link>
                </Text>
                <Text size="sm" c="dimmed">{vs.difference}</Text>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* Kibbe Connection */}
      <Box mb="xl" p="lg" bg="gray.0" style={{ borderRadius: 8 }}>
        <Title order={2} fz="lg" mb="sm">
          Kibbe Body Type Connection
        </Title>
        <Text mb="md">{essenceData.kibbeConnection}</Text>
        <Link href="/kibbe-body-type-test" style={{ color: "inherit", fontWeight: 500 }}>
          Take the Kibbe Body Type Test →
        </Link>
      </Box>

      <Divider my="xl" />

      {/* FAQ Section */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          Frequently Asked Questions
        </Title>
        <Accordion variant="separated">
          {essenceData.faqs.map((faq, i) => (
            <Accordion.Item key={i} value={`faq-${i}`}>
              <Accordion.Control>
                <Text fw={500}>{faq.question}</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed" style={{ lineHeight: 1.7 }}>
                  {faq.answer}
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Box>

      <Divider my="xl" />

      {/* Related Essences */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          Explore Other Style Essences
        </Title>
        <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
          {essenceIds
            .filter((id) => id !== essenceData.id)
            .map((id) => (
              <Link
                key={id}
                href={`/style-essence/${id}`}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  padding: "12px 16px",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Text fw={500}>{styleEssences[id].name}</Text>
                <IconChevronRight size={16} color="var(--mantine-color-dimmed)" />
              </Link>
            ))}
        </SimpleGrid>
      </Box>

      <Divider my="xl" />

      {/* CTA - Photo Upload */}
      <Box ta="center" py="xl">
        <Title order={2} fz="xl" mb="sm">
          Get Your Personalized Style Analysis
        </Title>
        <Text c="dimmed" mb="xl" maw={500} mx="auto">
          Style essences are just one piece of the puzzle. Upload a photo to discover your color season,
          which complements your {essenceData.name} essence perfectly.
        </Text>
        <QuizPhotoUpload quizName="Style Essence Page" />
      </Box>
    </Container>
  );
}
